import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, Envelope, Gift } from "@phosphor-icons/react";

export default function GiftCard() {
  const { token } = useParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    api.get(`/gift-cards/${token}`)
      .then(r => setCard(r.data))
      .catch(err => setError(err.response?.data?.detail || "This card could not be found."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-[80vh] bg-[#1A1A1A] text-[#FAF8F5] flex flex-col items-center justify-center px-6" data-testid="gift-card-not-found">
        <Envelope size={40} weight="light" className="mb-6 text-[#C97F8C]" />
        <p className="font-serif text-3xl mb-4 text-center">{error}</p>
        <Link to="/" className="overline underline underline-offset-4 mt-6 text-[#C97F8C]">Visit Iamravishing</Link>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-[80vh] bg-[#1A1A1A] text-[#FAF8F5] flex items-center justify-center" data-testid="gift-card-loading">
        <div className="overline text-[#C97F8C] animate-pulse">Opening…</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#1A1A1A] text-[#FAF8F5] relative overflow-hidden" data-testid="gift-card-page">
      {/* Floating hearts */}
      {opened && (
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className="absolute text-[#C97F8C]/70"
              style={{
                left: `${(i * 137) % 100}%`,
                bottom: "-40px",
                fontSize: `${16 + (i % 4) * 8}px`,
                animation: `giftfloat ${8 + (i % 5) * 2}s linear ${i * 0.4}s infinite`,
              }}
            >
              ♥
            </span>
          ))}
        </div>
      )}

      <style>{`
        @keyframes giftfloat {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.85; }
          90% { opacity: 0.85; }
          100% { transform: translateY(-110vh) rotate(30deg); opacity: 0; }
        }
        @keyframes envelopeShine {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
      `}</style>

      <div className="max-w-2xl mx-auto px-6 py-16 lg:py-24 relative z-10">
        {!opened ? (
          <div className="text-center" data-testid="gift-card-envelope">
            <div className="overline text-[#C97F8C] mb-6 tracking-[0.3em]">Iamravishing · A private note</div>
            <h1 className="font-serif text-5xl md:text-6xl leading-tight mb-6">
              For <em className="text-[#C97F8C] font-light">{card.recipient_name || "you"}</em>.
            </h1>
            <p className="text-[#C0BAB1] text-lg leading-relaxed mb-16 max-w-md mx-auto">
              {card.sender_name || "Someone"} sent you something quiet.<br />
              A parcel is on its way. Until then — open the note.
            </p>

            <button
              onClick={() => setOpened(true)}
              className="group inline-block relative"
              data-testid="gift-open-btn"
              style={{ animation: "envelopeShine 3s ease-in-out infinite" }}
            >
              <div className="w-48 h-32 md:w-56 md:h-36 bg-[#FAF8F5] relative shadow-2xl transition-transform duration-500 group-hover:-translate-y-2">
                <div className="absolute inset-0 border-b-[80px] md:border-b-[92px] border-l-[96px] md:border-l-[112px] border-r-[96px] md:border-r-[112px] border-transparent border-t-[#4A1525] border-t-[64px] md:border-t-[72px]" />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[#FAF8F5] flex items-end justify-center pb-4">
                  <Heart size={22} weight="fill" color="#4A1525" />
                </div>
              </div>
              <div className="mt-8 overline text-[#FAF8F5] tracking-[0.3em] group-hover:text-[#C97F8C] transition-colors">Tap to open</div>
            </button>
          </div>
        ) : (
          <div className="animate-[fadein_0.9s_ease-out]" data-testid="gift-card-opened">
            <style>{`@keyframes fadein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <div className="text-center mb-12">
              <div className="overline text-[#C97F8C] mb-3 tracking-[0.3em]">A note for {card.recipient_name || "you"}</div>
              <Heart size={36} weight="light" color="#C97F8C" className="mx-auto" />
            </div>

            {card.note ? (
              <blockquote className="font-serif italic text-2xl md:text-4xl leading-snug text-center text-[#FAF8F5] mb-12 px-4" data-testid="gift-card-note">
                &ldquo;{card.note}&rdquo;
              </blockquote>
            ) : (
              <p className="font-serif italic text-xl text-center text-[#C0BAB1] mb-12">
                They didn&apos;t write anything — the gift speaks for itself.
              </p>
            )}

            <div className="text-center mb-14">
              <div className="overline text-[#C97F8C] tracking-[0.3em]">— {card.sender_name || "A loved one"}</div>
            </div>

            <div className="border-t border-[#4A1525]/40 pt-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {card.gift_product_image && (
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={card.gift_product_image} alt={card.gift_product_name} className="w-full h-full object-cover" data-testid="gift-product-image" />
                  </div>
                )}
                <div>
                  <div className="overline text-[#C97F8C] mb-3">What&apos;s coming</div>
                  <div className="font-serif text-3xl mb-2 leading-tight" data-testid="gift-parent-name">{card.parent_product_name}</div>
                  <div className="text-[#C0BAB1] text-sm mb-6">
                    Paired with <span className="italic text-[#FAF8F5]">{card.gift_product_name}</span>
                  </div>
                  <p className="text-[#C0BAB1] leading-relaxed mb-8 text-sm">
                    Reconnect is a guided evening for two — designed with a licensed intimacy therapist. Your parcel will arrive discreetly in the coming days.
                  </p>
                  <Link to="/reconnect" className="inline-flex items-center gap-2 border border-[#FAF8F5] text-[#FAF8F5] px-6 py-3 overline text-xs hover:bg-[#FAF8F5] hover:text-[#1A1A1A] transition-colors" data-testid="gift-card-learn-more">
                    <Gift size={14} weight="light" /> Read the story
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-16 text-center">
              <div className="overline text-[#5C5C5C] tracking-[0.3em]">Iamravishing · Kept between the two of you</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
